import styles from './track-title-column.module.css'

import React, { useMemo } from 'react'

import { SEARCH_PAGE_LOCATION } from '@disco/state-management/selectors/media'
import { getNestedTracksCount } from '@disco/utils/tracks'

import type { Track } from 'types/track'

import MediaOverlay from 'components/common/media-overlay'
import PlayerPlay from 'components/common/player/player-play'
import TrackArtwork from 'components/common/tracks/track-artwork'
import { Badge, Button, Flex, Text } from 'components/ds'
import type { SearchColumnProps } from 'components/search/search-columns/types'
import { HoverProvider } from 'components/ui/hover-provider'

// The cell's own click selects the row - playing shouldn't.
const stopPropagation = (event: React.MouseEvent) => event.stopPropagation()

export const TrackTitleColumn = React.memo(({ info }: SearchColumnProps) => {
  const name = info.getValue<string>()
  const track = info.row.original as Track

  // Get tracklist from table meta (passed from SearchPage via DataTable)
  const tracklist = (info.table.options.meta as { tracklist?: number[] })
    ?.tracklist

  const playbackContext = useMemo(
    () => ({
      type: track.type,
      playbackLocation: SEARCH_PAGE_LOCATION,
      tracklist,
    }),
    [track.type, tracklist],
  )

  const columnSizeVar = useMemo(
    () =>
      `var(--col-${String(info.column.id)
        .toLowerCase()
        .replace(/\s+/g, '-')}-size)`,
    [info.column.id],
  )

  // Dynamic width computed from CSS variable for column sizing
  const dynamicWidth = `calc(${columnSizeVar} * 1px - 32px)`

  const isNestedRow = info.row.depth > 0

  if (isNestedRow) {
    // Same width as a parent row: the table is table-layout: auto, so asking
    // for a wider cell here widens the whole column as soon as a row expands.
    return (
      <Flex align="center" gap="2" width={dynamicWidth}>
        <Text size="1" color="gray" className={styles.nestedPosition}>
          {(track.nesting_position ?? 0) + 1}
        </Text>
        <Flex onClick={stopPropagation} flexShrink="0">
          <PlayerPlay
            trackId={track.id}
            mediaSource="in-app"
            playbackContext={playbackContext}
            size="1"
          />
        </Flex>
        <Flex className={styles.titleText} flexGrow="1" minWidth="0">
          <Text translate="no">{name}</Text>
        </Flex>
        {track.nesting_label && (
          <Badge color="gray" variant="outline" className={styles.nestedLabel}>
            {track.nesting_label}
          </Badge>
        )}
      </Flex>
    )
  }

  // Counted off the parent, so the pill shows before the children load.
  const canNest =
    Boolean(info.table.options.getSubRows) && getNestedTracksCount(track) > 0
  const isExpanded = info.row.getIsExpanded()

  return (
    <Flex align="center" gap="2" width={dynamicWidth}>
      <HoverProvider>
        <Flex width="24px" height="24px" flexShrink="0">
          <MediaOverlay
            track={track}
            mediaSource="in-app"
            playbackContext={playbackContext}
            iconSize={12}
          >
            <TrackArtwork track={track} />
          </MediaOverlay>
        </Flex>
      </HoverProvider>
      <Flex
        className={styles.titleText}
        flexGrow="1"
        minWidth="0"
        translate="no"
      >
        <Text>{name}</Text>
      </Flex>
      {canNest && (
        <Button
          variant="solid"
          size="1"
          className={styles.nestedCountButton}
          aria-expanded={isExpanded}
          aria-label={
            isExpanded
              ? `Collapse versions and stems for ${name}`
              : `Expand versions and stems for ${name}`
          }
          onClick={e => {
            // The cell's own click selects the row - expanding shouldn't.
            e.stopPropagation()
            info.row.toggleExpanded()
          }}
        >
          +{getNestedTracksCount(track)}
        </Button>
      )}
    </Flex>
  )
})

TrackTitleColumn.displayName = 'TrackTitleColumn'
